import {
  format as dateFormat,
  formatDistanceToNow as dateFormatDistanceToNow,
  intlFormatDistance as dateIntlFormatDistance,
} from '@owl-nest/date-fns'
// @ts-expect-error: types for date-fns-tz are badly configured and can't be
// imported
import * as dateFnsTz from 'date-fns-tz'
import * as env from '@owl-nest/config'
import * as supports from '@owl-nest/feature-detection'

import { locales, defaultLocaleName } from './locales'

type Locales = typeof locales
type LocaleName = keyof Locales
type Locale = Locales[LocaleName]

const DEFAULT_MONTH_LIST = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
]

export function daysFromNow(daysToAdd = 7): number {
  const date = new Date()
  return date.setDate(date.getDate() + daysToAdd)
}

/**
 * Formats a given date using the provided Unicode token.
 *
 * @remarks
 * A locale may be provided through the options object.
 * If none, this uses the global setting stored window.UFE.locale, otherwise falls back to English.
 *
 * @param date
 * @param formatStr string representation of the Unicode token to use for formatting
 * @param options
 */
export function format(date: Date | number, formatStr: string, options?: { locale?: string }): string {
  return dateFormat(date, formatStr, {
    ...options,
    locale: locales[getLocaleName(options?.locale)],
  })
}

/**
 * Expresses the duration between a given date and now in words.
 *
 * @remarks
 * A locale may be provided through the options object.
 * If none, this uses the global setting stored window.UFE.locale, otherwise falls back to English.
 *
 * @param date
 * @param options
 */
export function formatDistanceToNow(
  date: Date | number,
  options?: {
    addSuffix?: boolean
    includeSeconds?: boolean
    locale?: Locale
  },
): string {
  return dateFormatDistanceToNow(date, {
    ...options,
    locale: locales[getLocaleName(options?.locale)],
  })
}

export function intlFormatDistance(
  date: Date | number,
  baseDate: Date | number = Date.now(),
  options?: {
    locale?: string
    numeric?: 'always' | 'auto'
    style?: 'long' | 'narrow' | 'short'
  },
) {
  return dateIntlFormatDistance(date, baseDate, {
    locale: locales[getLocaleName(options?.locale)].code,
    numeric: 'always',
    style: 'short',
    ...options,
  })
}

/**
 * Returns a future date from now, adding the given amount of minutes.
 *
 * @param minutesToAdd defaults to 30
 */
export function minutesFromNow(minutesToAdd = 30): number {
  const now = Date.now() // current millisecond timestamp
  return now + minutesToAdd * 60 * 1000
}

export {
  addBusinessDays,
  addDays,
  addHours,
  addMonths,
  compareAsc,
  sub,
  isAfter,
  isBefore,
  differenceInCalendarDays,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInMonths,
  differenceInSeconds,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  getHours,
  getMinutes,
  getSeconds,
  parse,
  parseISO,
  setHours,
  setMinutes,
  startOfWeek,
  subDays,
  subMonths,
} from '@owl-nest/date-fns'
export * from './getCountDown'

export function getLocaleName(locale: Locale | string | undefined): LocaleName {
  const configLocale = typeof locale === 'string' ? locale : env.getUserConfig().lang
  if (configLocale !== undefined && isLocaleName(configLocale)) {
    return configLocale
  }
  console.error(
    `${configLocale} was not found in date-fns locales (${Object.keys(locales).join(
      ', ',
    )}). Defaulting to ${defaultLocaleName}`,
  )
  return defaultLocaleName
}

export function isLocaleName(locale: string): locale is LocaleName {
  return locale in locales
}

export function getMonthsList(locale?: Locale | string | undefined): string[] {
  let formatLocale: Locale

  if (typeof locale === 'string') {
    formatLocale = locales[getLocaleName(locale)]
  }

  return Array.from({ length: 12 }, (_, index) => {
    if (supports.intlDateTimeFormat() && typeof locale === 'string') {
      try {
        return new Intl.DateTimeFormat(locale, { month: 'long' }).format(new Date(2020, index, 1))
      } catch (error) {
        /* swallow error, fallback outside the catch */
      }
    }

    return formatLocale?.localize?.month(index, { width: 'abbreviated' }) ?? DEFAULT_MONTH_LIST[index]
  })
}

export function createDateWithTimezone(date: Date | string, timezone: string): Date {
  return dateFnsTz.utcToZonedTime(date, timezone)
}

export function getTimezoneCountry(timezone: string): string {
  if (timezone.includes('/')) {
    const timezoneSplited = timezone.split('/')
    return timezoneSplited[1]
  }

  return timezone
}

export function formatTime(date: Date, format: string | undefined = 'HH:mm'): string {
  return dateFnsTz.format(date, format)
}

export function zonedTimeToUtc(date: Date | string, timezone: string) {
  return dateFnsTz.zonedTimeToUtc(date, timezone)
}

export function utcToZonedTime(date: Date | string, timezone: string) {
  return dateFnsTz.utcToZonedTime(date, timezone)
}

export function getRelativeFormattedDateFromOffset(relativeDate: Date, offset = 60 * 60 * 24 * 1000) {
  return relativeDate.getTime() > Date.now() - offset ? intlFormatDistance(relativeDate) : format(relativeDate, 'P')
}

// Start of Selection
/**
 * Get the date format pattern for the given locale.
 * @example
 *     getDateFormatPattern('en-AU');   // dd/MM/yyyy
 *     getDateFormatPattern('en-US');   // M/d/yyyy
 */
export function getDateFormatPattern(locale: Intl.LocalesArgument) {
  if (supports.intlDateTimeFormat() && supports.dateTimeFormatToParts()) {
    return new Intl.DateTimeFormat(locale).formatToParts(new Date('2021-01-01')).map(getPatternForPart).join('')
  }

  return env.USER_COUNTRY === 'FR' ? 'dd/MM/yyyy' : 'MM/dd/yyyy'
}

function getPatternForPart(part: Intl.DateTimeFormatPart) {
  if (supports.dateTimeFormatToParts()) {
    switch (part.type) {
      case 'day':
        return 'd'.repeat(part.value.length)
      case 'month':
        return 'M'.repeat(part.value.length)
      case 'year':
        return 'y'.repeat(part.value.length)
      case 'literal':
        return part.value
      default:
        console.log('Unsupported date part', part)
        return ''
    }
  }

  console.log('Unsupported detectDateTimeFormatToParts')
  return ''
}
